import type { GameModeId } from './types';

const LEGACY_HIGH_SCORE_KEY = 'codytris-high-score-v1';
const HIGH_SCORE_KEY_PREFIX = 'codytris-high-score-v2';
const LOCAL_STATS_KEY = 'codytris-local-stats-v1';

export interface LocalModeStats {
  games: number;
  totalScore: number;
  totalLines: number;
  quads: number;
  bestLines: number;
}

type LocalStatsStore = Partial<Record<GameModeId, LocalModeStats>>;

const EMPTY_MODE_STATS: LocalModeStats = {
  games: 0,
  totalScore: 0,
  totalLines: 0,
  quads: 0,
  bestLines: 0,
};

function safeCounter(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
}

export function sanitizeLocalModeStats(value: unknown): LocalModeStats {
  if (!value || typeof value !== 'object') return { ...EMPTY_MODE_STATS };
  const candidate = value as Partial<LocalModeStats>;
  return {
    games: safeCounter(candidate.games),
    totalScore: safeCounter(candidate.totalScore),
    totalLines: safeCounter(candidate.totalLines),
    quads: safeCounter(candidate.quads),
    bestLines: safeCounter(candidate.bestLines),
  };
}

function readStatsStore(): LocalStatsStore {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(LOCAL_STATS_KEY) ?? '{}');
    return parsed && typeof parsed === 'object' ? parsed as LocalStatsStore : {};
  } catch {
    return {};
  }
}

function writeStatsStore(stats: LocalStatsStore): void {
  try {
    localStorage.setItem(LOCAL_STATS_KEY, JSON.stringify(stats));
  } catch {
    // Local stats are optional; storage failures must never interrupt gameplay.
  }
}

export function readModeStats(mode: GameModeId): LocalModeStats {
  return sanitizeLocalModeStats(readStatsStore()[mode]);
}

export function recordGameStart(mode: GameModeId): LocalModeStats {
  const store = readStatsStore();
  const current = sanitizeLocalModeStats(store[mode]);
  const next = { ...current, games: current.games + 1 };
  writeStatsStore({ ...store, [mode]: next });
  return next;
}

export function addGameProgress(
  mode: GameModeId,
  progress: { score: number; lines: number; quads: number; bestLines: number },
): LocalModeStats {
  const store = readStatsStore();
  const current = sanitizeLocalModeStats(store[mode]);
  const next: LocalModeStats = {
    games: current.games,
    totalScore: current.totalScore + safeCounter(progress.score),
    totalLines: current.totalLines + safeCounter(progress.lines),
    quads: current.quads + safeCounter(progress.quads),
    bestLines: Math.max(current.bestLines, safeCounter(progress.bestLines)),
  };
  writeStatsStore({ ...store, [mode]: next });
  return next;
}

export function readHighScore(mode: GameModeId = 'regular'): number {
  try {
    const stored = localStorage.getItem(`${HIGH_SCORE_KEY_PREFIX}:${mode}`)
      ?? (mode === 'regular' ? localStorage.getItem(LEGACY_HIGH_SCORE_KEY) : null)
      ?? '0';
    const value = Number.parseInt(stored, 10);
    return Number.isFinite(value) && value > 0 ? value : 0;
  } catch {
    return 0;
  }
}

export function writeHighScore(score: number, mode: GameModeId = 'regular'): void {
  try {
    localStorage.setItem(`${HIGH_SCORE_KEY_PREFIX}:${mode}`, String(score));
  } catch {
    // Storage can be unavailable in private browsing; gameplay should continue.
  }
}
